import { Component, Inject, PLATFORM_ID } from '@angular/core';
import { isPlatformBrowser } from '@angular/common';
import { CommonModule } from '@angular/common';
import { RouterModule } from '@angular/router';
import {
  ChildrenSelectorComponent,
  Student,
} from '../../shared/children-selector/children-selector.component';
import { TranslatePipe } from '@ngx-translate/core';
import { Avis, AvisService } from '../../services/avis.service';
import { PageTitleComponent } from '../../shared/page-title/page-title.component';
import { LoaderComponent } from '../../shared/loader/loader.component';
import { HttpClient } from '@angular/common/http';
import { NotificationService } from '../../services/notification.service';

interface UserData {
  iduser: number;
  role: number;
  idTeacher: number;
  orgid: number;
  testmobile: number;
  error?: string;
  listStudent?: Student[];
}

@Component({
  selector: 'app-avis',
  standalone: true,
  imports: [
    CommonModule,
    RouterModule,
    ChildrenSelectorComponent,
    TranslatePipe,
    PageTitleComponent,
    LoaderComponent,
  ],
  templateUrl: './avis.component.html',
  styleUrls: ['./avis.component.css'],
})
export class AvisComponent {
  currentPage = 1;
  pageSize = 10;
  totalPages = 1;

  students: Student[] = [];
  selectedStudent: Student | null = null;
  avis: Avis[] = [];
  userData: UserData | null = null;
  loading = false;

  constructor(
    @Inject(PLATFORM_ID) private platformId: Object,
    private avisService: AvisService,
    private http: HttpClient,
    private notificationService: NotificationService,
  ) {}

  ngOnInit() {
    if (isPlatformBrowser(this.platformId)) {
      const userDataStr = localStorage.getItem('userData');
      const studentListStr = localStorage.getItem('studentList');

      if (userDataStr) {
        try {
          this.userData = JSON.parse(userDataStr);
        } catch (e) {
          console.error('Failed to parse userData', e);
        }
      }

      if (studentListStr) {
        try {
          this.students = JSON.parse(studentListStr);
          if (this.students.length > 0 && !this.selectedStudent) {
            this.selectedStudent = this.students[0];
            this.loadStudentAvis(this.selectedStudent, 1);
          }
        } catch (e) {
          console.error('Failed to parse studentList', e);
        }
      }
    }
  }

  onStudentSelected(student: Student) {
    this.selectedStudent = student;
    this.currentPage = 1;
    this.loadStudentAvis(student, 1);
  }

  onStudentsLoaded(students: Student[]) {
    this.students = students;

    if (students.length > 0 && !this.selectedStudent) {
      this.selectedStudent = students[0];
      this.loadStudentAvis(students[0], 1);
    }
  }

  loadStudentAvis(student: Student, page: number) {
    if (!this.userData || !this.selectedStudent) return;

    this.currentPage = page;
    this.loading = true;

    this.avisService
      .getStudentAvis(student.batchId, page, this.pageSize)
      .subscribe({
        next: (res: any) => {
          // Check for new structure: response.results.observation
          let avisList: Avis[] = [];
          let totalPages = 1;

          if (res?.response?.results?.administrativDocumentBatchList) {
            // New structure for Avis: response.results.administrativDocumentBatchList[].administrativDocument
            avisList = res.response.results.administrativDocumentBatchList.map(
              (item: any) => item.administrativDocument || item,
            );
            totalPages = res.response.totalpage || 1;
          } else if (res?.response?.results?.observation) {
            // New structure fallback (just in case)
            avisList = res.response.results.observation;
            totalPages = res.response.totalpage || 1;
          } else if (Array.isArray(res?.response)) {
            // Fallback or old structure
            avisList = res.response.map(
              (item: any) => item.administrativDocument || item,
            );
            if (avisList.length > 0) {
              const total = avisList[0].nbrCirculaires || avisList.length;
              totalPages = Math.ceil(total / this.pageSize);
            }
          }

          this.avis = avisList;
          this.totalPages = totalPages;
          this.loading = false;
        },
        error: (err) => {
          console.error('❌ Error loading Avis:', err);
          this.avis = [];
          this.loading = false;
        },
      });
  }

  get pageNumbers(): number[] {
    return Array.from({ length: this.totalPages }, (_, i) => i + 1);
  }

  goToPage(page: number) {
    if (this.loading || page < 1 || page > this.totalPages) return;
    this.loadStudentAvis(this.selectedStudent!, page);
  }

  previousPage() {
    if (this.currentPage > 1) {
      this.goToPage(this.currentPage - 1);
    }
  }

  nextPage() {
    if (this.currentPage < this.totalPages) {
      this.goToPage(this.currentPage + 1);
    }
  }

  downloadAvis(item: Avis) {
    if (!item || !item.path) return;

    const baseUrl = 'https://plateforme.educanet.pro/';

    // Nettoyer le path pour garder uniquement la partie publique
    let cleanedPath = item.path;

    const publicFolder = 'CircularFile/';
    const index = cleanedPath.indexOf(publicFolder);

    if (index !== -1) {
      cleanedPath = cleanedPath.substring(index);
    }

    const downloadUrl = baseUrl + cleanedPath;
    
    const studentId = this.selectedStudent?.idStudent;
    const idAvis = item.id;

    if (studentId && idAvis) {
      this.notificationService
        .updateNotificationStatus({
          idType: idAvis,
          idStudent: studentId,
          nameType: 'Circulaire',
        })
        .subscribe({
          next: () => {
            // Mettre à jour l'état local pour refléter "vu"
            this.avis = this.avis.map((a) =>
              a.id === idAvis ? ({ ...a, vu: '1' } as Avis) : a,
            );
            if (this.userData) {
              this.notificationService
                .getNotificationCounts(this.userData.iduser.toString())
                .subscribe();
            }
          },
          error: (err) => console.error('Erreur updatenotif (Avis):', err),
        });
    }
    console.log('Opening download URL:', downloadUrl);
    window.open(downloadUrl, '_blank');
  }
}
